import {
  UploadBase,
  TRANSFER_TYPE_UPLOAD,
  TRANSFER_EVENT_START,
  TRANSFER_EVENT_PROGRESS,
  TRANSFER_EVENT_COMPLETE,
  TRANSFER_EVENT_CANCELED,
  TRANSFER_EVENT_FAILED,
} from './upload-base'

// Wraps a single Uppy file so the existing header tray (which speaks the
// UploadBase item contract) can render an Uppy upload. One instance per file;
// listens only to its own file's events on the shared-per-target Uppy instance.
// A picked file is INIT until Uppy starts sending it ('upload' /
// 'upload-start', or 'upload-retry' after a failure): only then is it an
// active transfer (transfer-status.js). An error is FAILED, not CANCELED.
export class UploadItem extends UploadBase {
  constructor(uppy, uppyFile, endpointGroup, targetId, gotoPath) {
    super(`${targetId}:${uppyFile.id}`, TRANSFER_TYPE_UPLOAD, uppyFile.name, gotoPath)
    this._uppy = uppy
    this._uppyFileID = uppyFile.id
    this._endpointGroup = endpointGroup
    this._targetId = targetId
    this._size = uppyFile.size || 0
    this._uploadTotal = uppyFile.size || 0

    this._onProgress = (file, progress) => {
      if (!file || file.id !== this._uppyFileID) return
      this._uploadLoaded = progress.bytesUploaded
      this._uploadTotal = progress.bytesTotal
      this.dispatchEvent(TRANSFER_EVENT_PROGRESS)
    }
    this._onSuccess = (file) => {
      if (!file || file.id !== this._uppyFileID) return
      this._uploadLoaded = this._uploadTotal
      this.notifySet()
      this.dispatchEvent(TRANSFER_EVENT_COMPLETE)
    }
    this._onError = (file, error) => {
      if (!file || file.id !== this._uppyFileID) return
      this._error = (error && error.message) || null
      this.dispatchEvent(TRANSFER_EVENT_FAILED)
    }
    // 'upload' (uploadID, files) and 'upload-start' (files) name the files
    // being sent; 'upload-retry' (file) resends a failed one.
    const mine = files => (files || []).some(f => f && f.id === this._uppyFileID)
    this._onStarted = (...args) => {
      const files = Array.isArray(args[0]) ? args[0] : args[1]
      if (!mine(files) || this.status === TRANSFER_EVENT_PROGRESS) return
      this._error = null
      this.dispatchEvent(TRANSFER_EVENT_START)
    }
    this._onRetry = (file) => {
      if (!file || file.id !== this._uppyFileID) return
      this._error = null
      this.dispatchEvent(TRANSFER_EVENT_START)
    }

    uppy.on('upload', this._onStarted)
    uppy.on('upload-start', this._onStarted)
    uppy.on('upload-retry', this._onRetry)
    uppy.on('upload-progress', this._onProgress)
    uppy.on('upload-success', this._onSuccess)
    uppy.on('upload-error', this._onError)
  }

  get size() { return this._size }
  get endpointGroup() { return this._endpointGroup }
  get targetId() { return this._targetId }
  get uppyFileID() { return this._uppyFileID }
  get error() { return this._error || null }

  detach() {
    this._uppy.off('upload', this._onStarted)
    this._uppy.off('upload-start', this._onStarted)
    this._uppy.off('upload-retry', this._onRetry)
    this._uppy.off('upload-progress', this._onProgress)
    this._uppy.off('upload-success', this._onSuccess)
    this._uppy.off('upload-error', this._onError)
  }

  cancel() {
    this.detach()
    try { this._uppy.removeFile(this._uppyFileID) } catch { /* already gone */ }
    this.dispatchEvent(TRANSFER_EVENT_CANCELED)
  }
}
